/**
 * CallMeToRecordDialog — POSTs to /api/recordings/call-me-to-record with
 * the caller's phone number; the server places an outbound call, the
 * caller speaks their recording, and a new recording row is created
 * asynchronously once they hang up.
 *
 * The endpoint returns a `{request_id, status}` payload for the in-flight
 * call rather than a full recording row, so on success this dialog shows
 * a "we're calling you now" notice and relies on the parent picker to
 * refetch when the user looks again. If the endpoint is ever changed to
 * hand back a full recording row synchronously, the onSuccess branch
 * below already forwards it via `onCreated`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordings, type RecordingRow } from '@/api/recordings';
import { verifiedNumbers, type VerifiedNumberRow, type VerifyResponse } from '@/api/verifiedNumbers';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { PhoneInput } from '@/components/PhoneInput';
import { PhoneCall, Info, CheckCircle2, ShieldCheck, X as XIcon, PhoneIncoming, Hash } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customerId?: string | null;
  folderId?: string | null;
  onCreated: (row: RecordingRow) => void;
}

export function CallMeToRecordDialog({
  open, onOpenChange, customerId: initialCustomerId, folderId, onCreated,
}: Props) {
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<'en' | 'he' | 'yi'>('en');
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId ?? null);
  const [message, setMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);

  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  // OTP entry state — only populated while a verification challenge is in
  // flight. `pendingNumberId` is the user_verified_numbers.id of the row
  // we're currently challenging. `pin` is what the user types after they
  // pick up the verify call.
  const [pendingNumberId, setPendingNumberId] = useState<string | null>(null);
  const [pendingNumberE164, setPendingNumberE164] = useState<string>('');
  const [pin, setPin] = useState('');

  // Verified-numbers list. The call-me backend rejects any destination that
  // isn't on this list (toll-fraud gate) with 403 number_not_verified. We
  // surface the list inline so Sam / end-users can add their own number
  // without leaving the dialog.
  const verifiedQuery = useQuery({
    queryKey: ['verified-numbers'],
    queryFn: () => verifiedNumbers.list(),
    enabled: open,
  });
  const verifiedList: VerifiedNumberRow[] = verifiedQuery.data?.rows ?? [];
  const verifiedActive = verifiedList.filter((r) => r.verified_at);
  const verifiedPending = verifiedList.filter((r) => !r.verified_at);
  const isPhoneVerified = verifiedActive.some((r) => r.e164 === phone.trim());
  // The pending (verified_at=NULL) row for the typed phone, if any.
  const pendingForPhone = verifiedPending.find((r) => r.e164 === phone.trim()) ?? null;

  const addVerified = useMutation({
    mutationFn: (e164: string) => verifiedNumbers.add(e164),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['verified-numbers'] });
      setMessage({
        kind: 'info',
        text: 'Number registered. Click "Call me to verify" to receive a 4-digit code.',
      });
      // Auto-advance: if there's no active challenge, hint at the next
      // action by surfacing the new row in the pending list. We don't
      // auto-start verification because that would burn a Polly synth
      // every time someone types a number.
      void data;
    },
    onError: (err) => {
      setMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Could not register the number.',
      });
    },
  });

  const startVerify = useMutation({
    mutationFn: (id: string) =>
      verifiedNumbers.startVerification(id, effectiveCustomerId ?? null),
    onSuccess: (_data, id) => {
      // Match the e164 from the existing row so we can show it in the
      // PIN-entry block without re-querying.
      const row = verifiedList.find((r) => r.id === id);
      setPendingNumberId(id);
      setPendingNumberE164(row?.e164 ?? phone.trim());
      setPin('');
      setMessage({
        kind: 'info',
        text: `Calling ${row?.e164 ?? phone.trim()} now. Pick up and listen for the 4-digit code, then type it below.`,
      });
    },
    onError: (err) => {
      const raw = err instanceof Error ? err.message : 'Could not start verification.';
      const friendly =
        raw.includes('reseller_recording_dial_in_e164_not_set')
          ? 'No reseller dial-in number is set. Ask your admin to configure it under Settings → Recording.'
          : raw.includes('rate_limited')
            ? 'Too many verification attempts. Wait a few minutes and try again.'
            : raw.includes('customer_id_required') || raw.includes('missing_customer_scope')
              ? 'Please pick a customer first.'
              : raw;
      setMessage({ kind: 'error', text: friendly });
    },
  });

  const submitPin = useMutation<VerifyResponse, Error, void>({
    mutationFn: async () => {
      if (!pendingNumberId) throw new Error('No verification in progress.');
      return verifiedNumbers.verify(pendingNumberId, pin.trim());
    },
    onSuccess: (result) => {
      if (result.ok) {
        qc.invalidateQueries({ queryKey: ['verified-numbers'] });
        setMessage({
          kind: 'info',
          text: 'Number verified. You can now use it for "Call me to record".',
        });
        setPendingNumberId(null);
        setPin('');
      } else if (result.kind === 'wrong') {
        setMessage({
          kind: 'error',
          text: `Wrong code. ${result.attemptsLeft} attempt${result.attemptsLeft === 1 ? '' : 's'} left.`,
        });
        setPin('');
      } else if (result.kind === 'expired') {
        setMessage({
          kind: 'error',
          text: 'The code expired (5-minute window). Click "Call me to verify" again.',
        });
        setPendingNumberId(null);
        setPin('');
      } else if (result.kind === 'locked') {
        setMessage({
          kind: 'error',
          text: 'Too many wrong attempts. Click "Call me to verify" to start over.',
        });
        setPendingNumberId(null);
        setPin('');
      } else {
        setMessage({
          kind: 'error',
          text: 'No verification in progress. Click "Call me to verify" first.',
        });
        setPendingNumberId(null);
        setPin('');
      }
    },
    onError: (err) => {
      const raw = err instanceof Error ? err.message : 'Verification failed.';
      const friendly = raw.includes('rate_limited')
        ? 'Too many verification attempts. Wait a minute and try again.'
        : raw;
      setMessage({ kind: 'error', text: friendly });
    },
  });

  const removeVerified = useMutation({
    mutationFn: (id: string) => verifiedNumbers.remove(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['verified-numbers'] });
      if (pendingNumberId === id) {
        setPendingNumberId(null);
        setPin('');
      }
    },
  });

  const callMe = useMutation({
    mutationFn: () => recordings.callMeToRecord({
      phone_number: phone.trim(),
      target: { mode: 'new', folder_id: folderId ?? null },
      ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
    }),
    onSuccess: (result) => {
      // If the endpoint hands us a full recording row synchronously,
      // forward it. Otherwise show the "we're calling you now" notice and
      // rely on the parent picker to refetch when the user looks again.
      const maybeRow = result as unknown as Partial<RecordingRow>;
      if (maybeRow && typeof maybeRow.id === 'string') {
        qc.invalidateQueries({ queryKey: ['recordings'] });
        onCreated(maybeRow as RecordingRow);
      } else {
        setMessage({
          kind: 'info',
          text: 'We are calling you now. Once you hang up, your recording will appear in the library — refresh the picker to see it.',
        });
        qc.invalidateQueries({ queryKey: ['recordings'] });
      }
    },
    onError: (err) => {
      const raw = err instanceof Error ? err.message : 'Could not place the call.';
      const friendly = raw.includes('number_not_verified')
        ? 'This number is not on your verified list. Add it above first, then try again.'
        : raw.includes('no_voice_carrier')
          ? 'No voice carrier is configured for this customer. Ask your admin to set one up.'
          : raw.includes('originate_failed')
            ? 'The carrier refused to place the call. Double-check the number and try again.'
            : raw;
      setMessage({ kind: 'error', text: friendly });
    },
  });

  // Lenient sanity check on the COMBINED value <PhoneInput> emits
  // (prefix + national digits). The backend accepts national input and
  // normalizes to E.164 server-side — this only catches obviously-broken
  // output (too short / too long) before we spend a network round-trip.
  const phoneValid = /^\+[1-9]\d{7,14}$/.test(phone.trim());
  const canSubmit =
    phoneValid
    && isPhoneVerified
    && name.trim().length > 0
    && effectiveCustomerId !== null
    && !callMe.isPending;

  function handleOpenChange(v: boolean) {
    if (callMe.isPending) return;
    if (!v) {
      setPhone('');
      setName('');
      setLanguage('en');
      setMessage(null);
      setPendingNumberId(null);
      setPendingNumberE164('');
      setPin('');
    }
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-emerald-600" />
            Call me to record
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {customerPicker}
          <CustomerScopeChip />

          <div className="rounded-md border border-blue-100 bg-blue-50/70 p-3 text-xs text-blue-700 flex gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <p>
              We will call one of your registered numbers. Speak your recording;
              when you hang up the audio is saved to the recordings library. Numbers
              must be registered below before we call them (anti-fraud gate).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-name">Name <span aria-hidden>*</span></Label>
            <Input
              id="cm-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Holiday greeting"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-phone">Phone number <span aria-hidden>*</span></Label>
            <PhoneInput
              id="cm-phone"
              value={phone}
              onChange={setPhone}
              customerId={effectiveCustomerId}
              placeholder="555 123 4567"
            />
            {phone.trim().length > 0 && !phoneValid && (
              <p className="text-[11px] text-red-500">
                Enter a valid phone number.
              </p>
            )}
            {phoneValid && isPhoneVerified && (
              <p className="flex items-center gap-1 text-[11px] text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> Number is verified.
              </p>
            )}
            {phoneValid && !isPhoneVerified && pendingNumberId !== (pendingForPhone?.id ?? null) && (
              <div className="rounded-md border border-amber-200 bg-amber-50/70 p-2.5 text-xs text-amber-800">
                <p className="mb-1.5 flex items-center gap-1.5 font-medium">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {pendingForPhone ? 'Registered but not yet verified.' : 'Not yet registered.'}
                </p>
                <p className="mb-2 leading-relaxed">
                  For safety, we only call numbers you own. We'll dial this number
                  and read a 4-digit code — type the code back in to verify.
                </p>
                {pendingForPhone ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={startVerify.isPending}
                    onClick={() => {
                      setMessage(null);
                      startVerify.mutate(pendingForPhone.id);
                    }}
                  >
                    {startVerify.isPending ? 'Calling…' : 'Call me to verify'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={addVerified.isPending}
                    onClick={() => { setMessage(null); addVerified.mutate(phone.trim()); }}
                  >
                    {addVerified.isPending ? 'Registering…' : 'Register this number'}
                  </Button>
                )}
              </div>
            )}

            {/* PIN entry — shown while a verification challenge is in flight
                for the number currently typed in the Phone field. */}
            {pendingNumberId && pendingNumberE164 === phone.trim() && (
              <div className="rounded-md border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900 space-y-2">
                <p className="flex items-center gap-1.5 font-medium">
                  <PhoneIncoming className="h-3.5 w-3.5" />
                  Verification call placed
                </p>
                <p className="leading-relaxed">
                  Pick up the call to {pendingNumberE164} and listen for the
                  4-digit code. Then type it below.
                </p>
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-blue-600 shrink-0" />
                  <Input
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="1234"
                    className="font-mono tracking-widest text-center"
                    inputMode="numeric"
                    maxLength={4}
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={pin.length !== 4 || submitPin.isPending}
                    onClick={() => { setMessage(null); submitPin.mutate(); }}
                  >
                    {submitPin.isPending ? 'Checking…' : 'Verify'}
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <button
                    type="button"
                    className="underline hover:no-underline disabled:opacity-50"
                    disabled={startVerify.isPending}
                    onClick={() => {
                      if (!pendingNumberId) return;
                      setMessage(null);
                      startVerify.mutate(pendingNumberId);
                    }}
                  >
                    {startVerify.isPending ? 'Calling again…' : 'Call again'}
                  </button>
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() => {
                      setPendingNumberId(null);
                      setPin('');
                      setMessage(null);
                    }}
                  >
                    Cancel verification
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Existing verified numbers — compact chip list. Clicking a chip
              copies the number into the Phone input. Removing a chip soft-
              deletes the row. Pending (verified_at=NULL) rows are shown in
              an amber strip below the verified list with a "Verify" action.
              */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">
              Your verified numbers
            </Label>
            {verifiedQuery.isLoading ? (
              <p className="text-xs text-slate-400">Loading…</p>
            ) : verifiedActive.length === 0 ? (
              <p className="text-xs text-slate-400">
                No numbers verified yet. Add one above.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {verifiedActive.map((n) => (
                  <span
                    key={n.id}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 py-0.5 pl-2 pr-1 text-xs font-mono text-emerald-800"
                  >
                    <button
                      type="button"
                      onClick={() => setPhone(n.e164)}
                      className="hover:underline"
                      aria-label={`Use ${n.e164}`}
                    >
                      {n.e164}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeVerified.mutate(n.id)}
                      disabled={removeVerified.isPending}
                      className="rounded-full p-0.5 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-900 disabled:opacity-50"
                      aria-label={`Remove ${n.e164}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Pending verification rows — surfaced so the user can pick
                up where they left off without re-typing the number. */}
            {verifiedPending.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {verifiedPending.map((n) => (
                  <span
                    key={n.id}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 py-0.5 pl-2 pr-1 text-xs font-mono text-amber-800"
                  >
                    <button
                      type="button"
                      onClick={() => setPhone(n.e164)}
                      className="hover:underline"
                      aria-label={`Use ${n.e164}`}
                    >
                      {n.e164}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPhone(n.e164);
                        setMessage(null);
                        startVerify.mutate(n.id);
                      }}
                      disabled={startVerify.isPending}
                      className="ml-1 rounded-full px-1.5 text-[10px] font-sans font-medium text-amber-700 hover:bg-amber-100 hover:text-amber-900 disabled:opacity-50"
                      aria-label={`Verify ${n.e164}`}
                    >
                      Verify
                    </button>
                    <button
                      type="button"
                      onClick={() => removeVerified.mutate(n.id)}
                      disabled={removeVerified.isPending}
                      className="rounded-full p-0.5 text-amber-600 hover:bg-amber-100 hover:text-amber-900 disabled:opacity-50"
                      aria-label={`Remove ${n.e164}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-lang">Language</Label>
            <Select value={language} onValueChange={(v) => setLanguage(v as typeof language)}>
              <SelectTrigger id="cm-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="he">Hebrew</SelectItem>
                <SelectItem value="yi">Yiddish</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {message && (
            <p
              role={message.kind === 'error' ? 'alert' : 'status'}
              className={
                message.kind === 'error'
                  ? 'text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2'
                  : 'text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2'
              }
            >
              {message.text}
            </p>
          )}
        </div>

        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={callMe.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => { setMessage(null); callMe.mutate(); }}
          >
            {callMe.isPending ? 'Calling…' : 'Call me now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
